'use client'

import { Button } from '@repo/studio-design-system-ui-button'

import { Link } from '../../../../navigation'
import type { DesktopOs } from '../../../../lib/links'
import { useDetectedOs } from '../../../../lib/use-detected-os'

/** Human-readable OS names. Proper nouns, intentionally not translated. */
const OS_LABEL: Record<DesktopOs, string> = {
  mac: 'macOS',
  windows: 'Windows',
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 384 512"
      className={className}
      fill="currentColor"
      aria-hidden
      focusable="false"
    >
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  )
}

function WindowsIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 448 512"
      className={className}
      fill="currentColor"
      aria-hidden
      focusable="false"
    >
      <path d="M0 93.7l183.6-25.3v177.4H0V93.7zm0 324.6l183.6 25.3V268.4H0v149.9zm203.8 28L448 480V268.4H203.8v177.9zm0-380.6v180.1H448V32L203.8 65.7z" />
    </svg>
  )
}

function OsIcon({ os, className }: { os: DesktopOs; className?: string }) {
  return os === 'windows' ? (
    <WindowsIcon className={className} />
  ) : (
    <AppleIcon className={className} />
  )
}

/**
 * Primary + secondary download buttons (bespoke light-marketing pills). The
 * primary targets the detected OS with a two-line dark pill, the secondary
 * offers the other OS as an outline button. Both route to `/thank-you?os=<os>`,
 * where a client island kicks off the real installer download — keeping these
 * pages statically rendered (no installer URL leaks into the static HTML).
 */
export function DownloadCta({
  forPrefix,
  alsoPrefix,
  btnSub,
}: {
  forPrefix: string
  alsoPrefix: string
  btnSub: string
}) {
  const os = useDetectedOs()
  const other: DesktopOs = os === 'mac' ? 'windows' : 'mac'

  return (
    <div className="flex flex-wrap items-center justify-center gap-[14px]">
      <Button
        asChild
        variant="overlay"
        // oxlint-disable-next-line cf-web/no-component-class-overrides -- stacked download CTA with custom icon sizing
        className="h-auto flex-col gap-0.5 rounded-[16px] px-8 py-[15px] shadow-[0_14px_36px_rgba(19,19,21,0.22)] transition-[transform,background-color] hover:-translate-y-px [&_svg]:mx-0 [&_svg]:size-[17px]"
      >
        <Link href={{ pathname: '/thank-you', query: { os } }}>
          <span className="inline-flex items-center gap-[11px] text-[17px] font-semibold">
            <OsIcon os={os} className="size-[17px]" />
            {forPrefix} {OS_LABEL[os]}
          </span>
          <span className="font-mono text-[11px] font-medium tracking-[0.02em] text-white/60">
            {btnSub}
          </span>
        </Link>
      </Button>

      <Link
        href={{ pathname: '/thank-you', query: { os: other } }}
        className="inline-flex items-center gap-[9px] rounded-[16px] border border-[rgba(19,19,21,0.18)] px-6 py-[18px] text-[15px] font-semibold text-sd-ink transition-colors hover:bg-[rgba(19,19,21,0.04)]"
      >
        <OsIcon os={other} className="size-[15px]" />
        {alsoPrefix} {OS_LABEL[other]}
      </Link>
    </div>
  )
}
